import React, { useMemo, useState } from "react";
import { AntTreeNodeCheckedEvent, TreeNodeNormal } from "antd/lib/tree/Tree";
import { Button, Empty, Icon, Input, Tree } from "antd";
import "antd/es/tree/style/index.css";
import style from "./index.module.scss";
import classNames from "classnames";
import _ from "underscore";

const { TreeNode } = Tree;

export interface TreeNodeType extends TreeNodeNormal {
    title: string;
    children?: TreeNodeType[];
}

interface TreeViewProps {
    treeData: TreeNodeType[];
    onSubmit: (keys: string[]) => void;
    onCancel: () => void;
    initValue?: string[];
    renderNode?: (node: TreeNodeType) => React.ReactNode;
}

export default function TreeView({
    treeData,
    initValue = [],
    onSubmit,
    onCancel,
    renderNode
}: TreeViewProps) {
    const [searchText, setSearchText] = useState("");
    const [checked, setChecked] = useState<string[]>(initValue);
    const [expandedKeys, setExpandedKeys] = useState<string[]>([]);

    // 所有选择的子节点
    const selectedLeaves = useMemo(() => {
        return checked.filter(item => !isRootNode(item));
    }, [checked]);

    // 所有选择的根节点
    const selectedRootIds = useMemo(
        function () {
            return _.reduce(
                treeData,
                (memo, course) => {
                    const children = _.pluck(course.children || [], "key");

                    const intersection = _.intersection(
                        children,
                        selectedLeaves
                    );
                    return intersection.length === children.length
                        ? [...memo, course.key]
                        : memo;
                },
                [] as string[]
            );
        },
        [treeData, selectedLeaves]
    );

    // 所有半选择的根节点
    const halfSelectedRootIds = useMemo(() => {
        return _.reduce(
            treeData,
            (memo, course) => {
                const children = _.pluck(course.children || [], "key");

                const intersection = _.intersection(children, checked);
                return intersection.length < children.length &&
                    intersection.length !== 0
                    ? [...memo, course.key]
                    : memo;
            },
            [] as string[]
        );
    }, [treeData, checked]);

    // 查询是否根节点
    function isRootNode(key: string) {
        return _.some(treeData, { key });
    }

    // 文本是否匹配搜索关键字
    function isMatchSearch(text: string, searchText: string) {
        return text.indexOf(searchText) !== -1;
    }

    // 课程是否匹配搜索关键字
    function isMatchSearchCourse(
        node: string,
        text: string,
        searchText: string
    ) {
        const course = _.find(treeData, { key: node });
        if (!course) return false;

        return (
            isMatchSearch(text, searchText) ||
            _.some(course.children || [], item =>
                isMatchSearch(item.title, searchText)
            )
        );
    }

    function onCheckTree(
        { checked, halfChecked }: any,
        evt: AntTreeNodeCheckedEvent
    ) {
        let newChecked: string[] = [...checked];
        const { node } = evt;
        const {
            props: { eventKey, children }
        } = node;

        // 点击的是根节点并且之前处于未选择状态
        if (isRootNode(eventKey!) && !selectedRootIds.includes(eventKey!)) {
            const childKeys = _.pluck(children || [], "key") as string[];
            newChecked.push.apply(newChecked, childKeys);
        }

        // 点击的是根节点并且之前处于已选择状态
        if (isRootNode(eventKey!) && selectedRootIds.includes(eventKey!)) {
            const childKeys = _.pluck(children || [], "key") as string[];
            newChecked = newChecked.filter(key => !childKeys.includes(key));
        }

        setChecked(_.uniq(newChecked));
    }

    // 相应输入框输入
    function searchChangeHandler(e: React.ChangeEvent<HTMLInputElement>) {
        const text = e.target.value;
        setSearchText(text);

        const expandedKeys = _.pluck(
            _.filter(treeData, item =>
                isMatchSearchCourse(item.key, item.title, text)
            ),
            "key"
        );
        setExpandedKeys(text !== "" ? expandedKeys : []);
    }

    return (
        <div className={style.root}>
            <Input
                prefix={<Icon type="search" />}
                placeholder="请输入搜索内容"
                onChange={searchChangeHandler}
                value={searchText}
            />

            <div className={style.tree}>
                <Tree
                    showIcon
                    defaultExpandAll={false}
                    checkedKeys={{
                        checked: [...selectedLeaves, ...selectedRootIds],
                        halfChecked: halfSelectedRootIds
                    }}
                    onCheck={onCheckTree}
                    checkStrictly
                    checkable
                    selectable={false}
                    expandedKeys={expandedKeys}
                    onExpand={setExpandedKeys}>
                    {treeData.map(course => (
                        <TreeNode
                            title={
                                renderNode ? renderNode(course) : course.title
                            }
                            key={course.key}
                            className={classNames(style.node, {
                                [style.hidden]: !isMatchSearchCourse(
                                    course.key,
                                    course.title,
                                    searchText
                                )
                            })}>
                            {course.children?.map(item => (
                                <TreeNode
                                    title={
                                        renderNode
                                            ? renderNode(item)
                                            : item.title
                                    }
                                    key={item.key}
                                    className={classNames(style.node, {
                                        [style.hidden]: !isMatchSearch(
                                            item.title,
                                            searchText
                                        )
                                    })}
                                />
                            ))}
                        </TreeNode>
                    ))}
                </Tree>

                {_.all(
                    treeData,
                    course =>
                        !isMatchSearchCourse(
                            course.key,
                            course.title,
                            searchText
                        )
                ) && <Empty />}
            </div>

            <div className={style.footer}>
                <Button type="default" onClick={onCancel}>
                    取消
                </Button>
                &nbsp;&nbsp;&nbsp;
                <Button type="primary" onClick={() => onSubmit(selectedLeaves)}>
                    确认
                </Button>
            </div>
        </div>
    );
}
